import React, { useEffect, useState } from "react";
import { Input, Option, Select } from "@material-tailwind/react";
import Navbar from "../AdminNav";
import { TrashIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import AddProduct from "./AddProduct";
import ErrorMsg from "./ErrorMsg";
import EditProduct from "./EditProduct";
import { MdClose } from "react-icons/md";
import {
  FETCH_CATEGORIES_API,
  FETCH_SUBCATEGORIES_API,
  FETCH_SUBCATEGORY_PRODUCTS_API,
  DELETE_PRODUCT_API,
} from "../../Api";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import DeleteConfirmation from "../AddClientCompononents/DeleteConfirmation";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddProducts = () => {
  // Auth check
  const navigate = useNavigate();
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    const isTokenExpired = (token) => {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decodedToken.exp < currentTime;
    };
    if (!authToken || isTokenExpired(authToken)) {
      navigate("/signin");
    }
  }, [navigate]);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [search, setSearch] = useState("");
  const [categoryName, setCategoryName] = useState(null);
  const [subcategoryName, setSubcategoryName] = useState(null);

  const [prodInfo, setProdInfo] = useState(null);
  const [addProduct, setAddProduct] = useState(false);
  const [editProduct, setEditProduct] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [mainImage, setMainImage] = useState(null); // main preview in modal

  // Add Product Btn
  const handleAddProductBtn = () => setAddProduct(!addProduct);

  // Edit
  const handleEditClick = (product) => {
    setProdInfo(product);
    setEditProduct(true);
  };

  // Delete
  const handleDeleteBtn = (product) => {
    setProdInfo(product);
    setDeleteConfirmation(true);
  };

  const deleteProduct = async (categoryId, subcategoryId, productId) => {
    try {
      await axios.delete(
        `${DELETE_PRODUCT_API}${categoryId}/${subcategoryId}/${productId}`
      );
            toast.success("Product deleted successfully");
    } catch (error) {
      console.error("Error deleting product:", error);
    } finally {
      setSelectedProduct(null);
      setDeleteConfirmation(false);
      setProdInfo(null);
      fetchProductsFromServer(subcategoryId);
    }
  };

  // Fetch categories
  async function fetchCategoriesFromServer() {
    try {
      const response = await fetch(FETCH_CATEGORIES_API);
      setCategories(await response.json());
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }

  // Fetch subcategories
  async function fetchSubCategoriesFromServer(categoryId) {
    try {
      const response = await fetch(FETCH_SUBCATEGORIES_API + categoryId);
      setSubCategories(await response.json());
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
    }
  }

  // Fetch products
  async function fetchProductsFromServer(subcategoryId) {
    if (!categoryName) return;
    try {
      const response = await fetch(
        `${FETCH_SUBCATEGORY_PRODUCTS_API}${categoryName.id}/${subcategoryId}`
      );
      setProducts(await response.json());
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }

  // Category click
  const handleCategoryClick = (category) => {
    setCategoryName(category);
    fetchSubCategoriesFromServer(category.id);
  };

  // Subcategory click
  const handleSubCategoryClick = (subCategory) => {
    setSubcategoryName(subCategory);
    fetchProductsFromServer(subCategory.id);
  };

  useEffect(() => {
    fetchCategoriesFromServer();
  }, []);

  return (
    <div>
      <Navbar />

      {/* Add Product Modal */}
      {addProduct &&
        (categoryName ? (
          subcategoryName ? (
            <AddProduct
              categoryId={categoryName.id}
              subCategoryId={subcategoryName.id}
              onClose={() => {
                setAddProduct(false);
                fetchProductsFromServer(subcategoryName.id);
              }}
            />
          ) : (
            <ErrorMsg
              errMsg="Please select a Sub-category"
              onClose={() => setAddProduct(false)}
            />
          )
        ) : (
          <ErrorMsg
            errMsg="Please select a Category"
            onClose={() => setAddProduct(false)}
          />
        ))}

      {/* Edit Product Modal */}
      {editProduct && (
        <EditProduct
          prodData={prodInfo}
          categoryId={categoryName.id}
          subCategoryId={subcategoryName.id}
          onClose={() => {
            setSelectedProduct(null);
            setEditProduct(false);
            setProdInfo(null);
            fetchProductsFromServer(subcategoryName.id);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirmation && (
        <DeleteConfirmation
          onConfirm={() =>
            deleteProduct(categoryName.id, subcategoryName.id, prodInfo.id)
          }
          onCancel={() => setDeleteConfirmation(false)}
        />
      )}

      {/* Page Header */}
      <div className="px-4 md:px-6 mt-8 mb-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
            Manage Products
          </h2>

          <div className="flex flex-col sm:flex-row sm:space-x-4 w-full md:w-auto mt-3 md:mt-0">
            {/* Category */}
            <div className="w-full sm:w-48">
              <Select label="Category" className="w-full">
                {categories.map((category, index) => (
                  <Option
                    key={index}
                    value={category.name}
                    onClick={() => handleCategoryClick(category)}
                  >
                    {category.name}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Sub-category */}
            <div className="w-full sm:w-48">
              <Select label="Sub-category" className="w-full">
                {subCategories.map((subcat, index) => (
                  <Option
                    key={index}
                    value={subcat.name}
                    onClick={() => handleSubCategoryClick(subcat)}
                  >
                    {subcat.name}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Add Product Btn */}
            <div
              className="w-full sm:w-48 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg cursor-pointer"
              onClick={handleAddProductBtn}
            >
              <p className="text-sm">Add Product</p>
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      {products.length > 0 ? (
        <>
          {/* Search */}
          <div className="px-4">
            <Input
              label="Search for Products here..."
              type="text"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
            {products
              .filter(
                (item) =>
                  search === "" ||
                  item.name.toLowerCase().includes(search.toLowerCase())
              )
              .map((item, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedProduct(item);
                    setMainImage(item.img[0]); // default main image
                  }}
                  className="bg-white rounded-xl shadow-md p-2 border cursor-pointer hover:scale-105 transition"
                >
                  <p className="text-lg font-bold">{item.name}</p>
                  <p className="text-sm text-gray-600 line-clamp-3 mt-1">
                    {item.desc}
                  </p>
                  <div
                    className="w-full h-48 mt-3 rounded-lg bg-cover bg-center"
                    style={{ backgroundImage: `url(${item.img[0]})` }}
                  >
                    <div className="w-full h-full bg-black bg-opacity-30 rounded-lg"></div>
                  </div>
                </div>
              ))}
          </div>

          {/* Product Modal */}
          {selectedProduct && (
            <div className="fixed inset-0 z-50 flex">
              {/* Left Overlay */}
              <div
                className="hidden sm:block flex-grow bg-black bg-opacity-50"
                onClick={() => setSelectedProduct(null)}
              ></div>

              {/* Side Panel */}
              <div className="relative h-full w-full sm:w-[650px] bg-white shadow-lg flex flex-col">
                {/* Close Btn */}
                <button
                  className="absolute top-4 right-4 text-gray-600 hover:text-red-500"
                  onClick={() => setSelectedProduct(null)}
                >
                  <MdClose className="w-6 h-6" />
                </button>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  <h2 className="text-lg font-bold">{selectedProduct.name}</h2>
                  <hr className="my-2" />

                  {/* Image Gallery */}
                  <div className="w-full">
                    {/* Main Image */}
                    <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden shadow-lg">
                      <img
                        src={mainImage}
                        alt="Product"
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Thumbnails */}
                    <div className="flex gap-3 mt-4 flex-wrap">
                      {selectedProduct.img.map((img, index) => (
                        <div
                          key={index}
                          className={`w-20 h-20 rounded-md overflow-hidden border cursor-pointer ${
                            mainImage === img ? "ring-2 ring-green-500" : ""
                          }`}
                          onClick={() => setMainImage(img)}
                        >
                          <img
                            src={img}
                            alt={`thumb-${index}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mt-4">
                    <p className="text-gray-800">{selectedProduct.desc}</p>
                  </div>

                  {/* Features */}
                  <div className="mt-4 space-y-2">
                    {selectedProduct.feat.map((feat, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 shadow p-3 rounded-lg relative"
                      >
                        <span className="absolute left-0 top-0 h-full w-1 bg-green-500 rounded-l-lg"></span>
                        <p className="ml-2 text-sm text-gray-700">{feat}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer Btns */}
                <div className="flex gap-3 p-4 border-t">
                  <button
                    className="flex-1 flex items-center justify-center bg-green-500 text-white py-2 rounded-lg hover:bg-green-600"
                    onClick={() => handleEditClick(selectedProduct)}
                  >
                    <PencilSquareIcon className="w-5 h-5 mr-2" /> Edit
                  </button>
                  <button
                    className="flex-1 flex items-center justify-center bg-red-500 text-white py-2 rounded-lg hover:bg-red-600"
                    onClick={() => handleDeleteBtn(selectedProduct)}
                  >
                    <TrashIcon className="w-5 h-5 mr-2" /> Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="flex justify-center items-center mt-60">
          <p className="text-2xl text-gray-600">
            Manage your site products by updating or adding a new product.
          </p>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default AddProducts;
